import { ArrowPathIcon, ClockIcon, PlayIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { AiSchedule, AiScheduleRunStatus, deleteAiSchedule, runAiScheduleNow, updateAiSchedule } from 'services'
import { Button, Toggle } from 'stories/components'
import { formatTime } from 'utils'
import { getActiveTimeZone } from 'utils/timezone'

import { ScheduleDialog } from './ScheduleDialog'
import { ScheduleRunsModal } from './ScheduleRunsModal'

const STATUS_STYLE: Record<AiScheduleRunStatus, string> = {
  queued: 'bg-slate-100 text-slate-600',
  running: 'bg-sky-50 text-sky-700',
  success: 'bg-green-50 text-green-700',
  error: 'bg-red-50 text-red-700',
  skipped: 'bg-gray-100 text-gray-600',
}

const STATUS_LABEL: Record<AiScheduleRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Last run fine',
  error: 'Last run failed',
  skipped: 'Last run skipped',
}

/**
 * The schedule's own zone, named only when it is not the viewer's.
 *
 * Every timestamp on this card is rendered in the viewer's zone, so a Chicago schedule otherwise
 * reads "Every Monday at 8:30 AM" directly above "Next 10/19/2026 09:30" - two true lines that look
 * like a bug.
 */
const zoneSuffix = (timezone?: string) => {
  if (!timezone || timezone === getActiveTimeZone()) return ''
  return ` ${String(timezone).split('/').pop()?.replace(/_/g, ' ')} time`
}

/** How a fan-out schedule's loan set reads on one line. */
const describeTarget = (schedule: AiSchedule) => {
  const tags = schedule.loanFilters?.tags || []
  const match = schedule.loanFilters?.tagMatch === 'all' ? 'all of' : 'any of'
  if (!tags.length) return 'a set of loans'
  return tags.length === 1 ? `loans tagged ${tags[0]}` : `loans tagged ${match} ${tags.join(', ')}`
}

interface SchedulesPanelProps {
  projectId: number
  /** Set when the workspace is open inside a loan; new schedules are then fixed to that loan. */
  loanNumber?: number
  schedules: AiSchedule[]
  /** The project owner may change anyone's schedule; everyone else only their own. */
  isProjectOwner: boolean
  userEmail: string
  onChanged: () => void
  onOpenConversation?: (conversationId: number) => void
}

/**
 * The project's scheduled runs: a prompt this project answers on a cadence, with each answer kept
 * as chat.
 *
 * Anyone the project is shared with can read the schedules and start a run; only the person who
 * created a schedule - or the project's owner - can change or delete it, because a run executes
 * with its creator's permissions.
 */
export function SchedulesPanel({
  projectId,
  loanNumber,
  schedules,
  isProjectOwner,
  userEmail,
  onChanged,
  onOpenConversation,
}: SchedulesPanelProps) {
  const [editing, setEditing] = useState<{ schedule: AiSchedule | null } | null>(null)
  const [showRunsFor, setShowRunsFor] = useState<AiSchedule | null>(null)
  const [startingId, setStartingId] = useState<number | null>(null)

  const canManage = (schedule: AiSchedule) =>
    isProjectOwner || String(schedule.createdBy || '').toLowerCase() === String(userEmail).toLowerCase()

  const onToggleEnabled = async (schedule: AiSchedule) => {
    await updateAiSchedule(schedule.id, { isEnabled: !schedule.isEnabled }).catch(() => null)
    onChanged()
  }

  const onRunNow = async (schedule: AiSchedule) => {
    setStartingId(schedule.id)
    try {
      await runAiScheduleNow(schedule.id)
      // The answer is not back yet - the run row is. The runs view polls it from here.
      setShowRunsFor(schedule)
      onChanged()
    } catch {
      // Surfaced by the global API error handler.
    } finally {
      setStartingId(null)
    }
  }

  const onDelete = async (schedule: AiSchedule) => {
    if (!window.confirm(`Delete the schedule "${schedule.name}"? Its past chats are kept.`)) return
    await deleteAiSchedule(schedule.id)
    toast(`"${schedule.name}" will not run again.`, { type: 'success' })
    onChanged()
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <Button size="sm" onClick={() => setEditing({ schedule: null })}>
          <span className="flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" />
            New schedule
          </span>
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {!schedules.length && (
          <p className="text-sm text-gray-500 p-3">
            Nothing scheduled yet. A schedule runs one prompt in this project on its own - every morning,
            every Monday, once next week - and keeps each answer as a chat
            {loanNumber ? ` on loan ${loanNumber}.` : ' here in the project.'}
          </p>
        )}

        {schedules.map((schedule) => {
          const manageable = canManage(schedule)
          const status = schedule.lastStatus as AiScheduleRunStatus | undefined
          return (
            <div key={schedule.id} className="border border-gray-200 rounded-md p-2.5 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${schedule.isEnabled ? 'text-gray-800' : 'text-gray-400'}`}>
                    {schedule.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5 shrink-0" />
                    {schedule.description || schedule.frequency}
                    {zoneSuffix(schedule.timezone)}
                  </p>
                </div>
                {manageable ? (
                  <div className="shrink-0">
                    <Toggle
                      id={`schedule-${schedule.id}`}
                      value={schedule.isEnabled}
                      onChange={() => onToggleEnabled(schedule)}
                    />
                  </div>
                ) : (
                  !schedule.isEnabled && <span className="shrink-0 text-xs text-gray-400">Paused</span>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-1">
                {schedule.isEnabled && schedule.nextRunAt
                  ? `Next ${formatTime(schedule.nextRunAt)}`
                  : 'Not scheduled to run'}
                {!!schedule.loanNumber && ` · Loan ${schedule.loanNumber}`}
                {!!schedule.loanFilters && ` · ${describeTarget(schedule)}`}
                {/* Inside a loan the list also carries the project's unscoped schedules, so say
                    which ones are not about this file. */}
                {!!loanNumber && !schedule.loanNumber && !schedule.loanFilters && ' · whole project'}
                {schedule.threadMode === 'perRun' ? ' · new chat each run' : ' · one ongoing chat'}
              </p>

              {!!status && (
                <p className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${STATUS_STYLE[status]}`}>
                    {STATUS_LABEL[status]}
                  </span>
                  {!!schedule.lastRunAt && <span className="text-[11px] text-gray-400">{formatTime(schedule.lastRunAt)}</span>}
                </p>
              )}
              {!!schedule.loanFilters && !!schedule.lastMatchedCount && (
                <p className="text-xs text-gray-500 mt-1">
                  {schedule.lastMatchedCount > schedule.maxLoansPerRun
                    ? `Last matched ${schedule.lastMatchedCount} loans; ran the first ${schedule.maxLoansPerRun}.`
                    : `Last matched ${schedule.lastMatchedCount} loan${schedule.lastMatchedCount === 1 ? '' : 's'}.`}
                </p>
              )}
              {status === 'error' && !!schedule.lastError && (
                <p className="mt-1 text-xs text-red-700 bg-red-50 rounded px-2 py-1">{schedule.lastError}</p>
              )}

              <div className="flex items-center gap-2.5 mt-2 pt-2 border-t border-gray-100">
                <button
                  className="text-xs text-shade-blue hover:underline flex items-center gap-1 disabled:opacity-50"
                  disabled={startingId === schedule.id}
                  onClick={() => onRunNow(schedule)}
                >
                  {startingId === schedule.id ? (
                    <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <PlayIcon className="w-3.5 h-3.5" />
                  )}
                  Run now
                </button>
                <button className="text-xs text-shade-blue hover:underline" onClick={() => setShowRunsFor(schedule)}>
                  Runs{schedule.runCount ? ` (${schedule.runCount})` : ''}
                </button>
                {manageable && (
                  <>
                    <button
                      className="text-xs text-shade-blue hover:underline"
                      onClick={() => setEditing({ schedule })}
                    >
                      Edit
                    </button>
                    <button
                      className="ml-auto p-1 text-gray-400 hover:text-red-600"
                      title="Delete schedule"
                      onClick={() => onDelete(schedule)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {editing && (
        <ScheduleDialog
          projectId={projectId}
          schedule={editing.schedule}
          lockedLoanNumber={loanNumber}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onChanged()
          }}
        />
      )}

      {showRunsFor && (
        <ScheduleRunsModal
          schedule={showRunsFor}
          onOpenConversation={onOpenConversation}
          onClose={() => {
            setShowRunsFor(null)
            // A finished run leaves a new chat and a new last-run status behind it.
            onChanged()
          }}
        />
      )}
    </div>
  )
}
